import React, { useState, useCallback, useEffect } from 'react';
import { array, number, string, func, bool, object } from 'prop-types';
import classNames from 'classnames';
import _get from 'lodash/get';
import { Button } from '_ui-core-js/React/components/Button.jsx';
import { AnimatedTextHeader } from '_ui-core-js/React/components/AnimatedTextHeader.jsx';
import { CssAnimation } from '_ui-core-js/React/components/CssAnimation.jsx';
import { Divider } from '_ui-core-js/React/components/Divider.jsx';
import { formatDoubleDigit } from '_ui-core-js/utilities/formatDoubleDigit.js';
import { multiplier } from '_ui-core-js/utilities/multiplier.js';
import { useSwipe } from '_ui-core-js/React/hooks/useSwipe.js';
import { CORE_GTM_TRACKING_VALUES } from '_ui-core-js/constants/analytics.js';
import { fireGTMTrackingEvent } from '_ui-core-js/utilities/fireGTMTrackingEvent.js';
import { AtmosphereHeroImages, LbxTheme } from '../../partials';
import { constants} from '../../constants'
import { LBX_GTM_TRACKING_VALUES } from '../../constants/analytics.js';
import { DeleteModal } from "./DeleteModal.jsx";

const { LBX_THEME_MAP } = constants

export function Showroom(props) {

  const {
    startPosition = 1,
    title,
    subtitle,
    items,
    deleteModal,
    onCardChange,
    isInfiniteScroll = true,
    inheritParentTheme,
    selectedAtmosphere,
    showReservePrompts,
    show360ExperienceLinks,
    user
  } = props;

  const [direction, setDirection] = useState();
  const [moving, setMoving] = useState(false);

  const startpoint = isInfiniteScroll
    ? items.length - 1 + startPosition
    : startPosition;

  const cards = 
    isInfiniteScroll
      ? [...items, ...items, ...items]
      : [...items]
  ;

  const activeIndex = (index) =>
    items.findIndex(({ id }) => id === cards[index].id);

  const [numberOfCards] = useState(cards.length)
  const [activeItem, setActiveItem] = useState(startpoint);
  const [leftScrollDisabled, setLeftScrollDisabled] = useState(isInfiniteScroll ? false : true);
  const [rightScrollDisabled, setRightScrollDisabled] = useState(false);
  const [theme, setTheme] = useState(selectedAtmosphere)
  const [initialised, setInitialised] = useState(false);

  const [activePosition, setActivePosition] = useState(activeIndex(activeItem));

  const block = 'c-showroom';
  const isMoreThanOneItem = items.length > 0;

  useEffect(() => {
    onCardChange && onCardChange(cards[activeItem].name);
    setTheme(cards[activeItem].name);
  }, [activeItem]);

  useEffect(() => {
    if (initialised) {
      fireGTMTrackingEvent({
        name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
        componentName: LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceCollection,
        componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
        action: CORE_GTM_TRACKING_VALUES.action.browseVersion,
        label: (cards[activeItem].name).toLowerCase()
      });
    } else {
      setInitialised(true);
    }
  }, [activeItem]);

  const swipeEvents = isMoreThanOneItem
    ? useSwipe((swipeRight, swipeLeft) => {
      if (swipeLeft) {
        moveLeft();
      }
      if (swipeRight) {
        moveRight();
      }
    })
    : null;

  const moveLeft = useCallback(() => {
    if (!leftScrollDisabled) {
      setMoving(true);
      setDirection('left');
      setActiveItem(activeItem - 1);
      setActivePosition(activeIndex(activeItem - 1));
    }
  }, [cards, activeItem, activePosition, leftScrollDisabled]);

  const moveRight = useCallback(() => {
    if (!rightScrollDisabled) {
      setMoving(true);
      setDirection('right');
      setActiveItem(activeItem + 1);
      setActivePosition(activeIndex(activeItem + 1));
    }
  }, [cards, activeItem, activePosition, rightScrollDisabled]);

  const moveTo = useCallback(
    (index) => {
      if (index < activeItem) {
        moveLeft();
      }

      if (index > activeItem) {
        moveRight();
      }
    },
    [
      cards,
      activeItem,
      activePosition,
      leftScrollDisabled,
      rightScrollDisabled
    ]
  );

  const updateScrollControls = useCallback(() => {
    const isFirstItem = activeItem === 0;
    const isLastItem = activeItem + 1 === numberOfCards;

    if (isFirstItem !== leftScrollDisabled) {
      setLeftScrollDisabled(isFirstItem);
    }

    if (isLastItem !== rightScrollDisabled) {
      setRightScrollDisabled(!!isLastItem);
    }
  }, [activeItem, numberOfCards, rightScrollDisabled, leftScrollDisabled]);

  const transitionEnd = useCallback(() => {
    setMoving(false);
    if (isInfiniteScroll) {
      if (direction === 'right' || activeItem > items.length * 2) {
        if (isInfiniteScroll) {
          setActiveItem(
            activeItem >= startpoint + items.length ? startpoint : activeItem
          );
        } else {
          updateScrollControls();
        }
      }

      if (direction === 'left' || activeItem < items.length) {
        setActiveItem(
          activeItem <= startpoint ? activeItem + items.length : activeItem
        );
      }
    } else {
      updateScrollControls();
    }
  }, [activeItem, setMoving, setActiveItem, direction, items]);

  const fontSize = useCallback((label) => {
    if (label.length <= 3) {
      return 'l-text';
    }

    if (label.length > 3 && label.length <= 6) {
      return 'm-text';
    }

    if (label.length > 6 && label.length <= 10) {
      return 's-text';
    }

    if (label.length > 10) {
      return 'xs-text';
    }
  }, []);

  return (
    <LbxTheme 
    theme={theme}
    inherit={inheritParentTheme}
    ignoreGlobalTheme={true}
    className={`${block} container-xxl utils__section-padding-small--uniform`}
    htmlTag={'article'}
    >
      <AnimatedTextHeader heading={title} subheading={subtitle} />
      <Divider />
      <nav className={`${block}__control-container`}>
        <CssAnimation
          enter={{
            name: 'short-slide-up-fade-in',
            duration: 0.25,
            delay: '0.75, hidden',
            easing: 'ease-out'
          }}
          triggerEnter={'in-view-init'}
          inViewProps={{ threshold: 0.2 }}
        >
          <div className={`${block}__tablist__container`} role='tablist'>
            <ul
              className={`${block}__slider ${moving ? 'transitioning' : ''}`}
              style={{transform: `translateX(-${activeItem * 100}%)`}}
              onTransitionEnd={transitionEnd}
              {...swipeEvents}
            >
              {cards.map((item, index) => {
                const itemName = item.alternateName || item.name;
                const isItemSelected = index === activeItem;

                return (
                  <li
                    className={`${block}__nav-item`}
                    key={index}
                  >
                    <button
                      role='tab'
                      tabIndex={-1}
                      onClick={() => moveTo(index)}
                      className={`${block}__button  apply-lbx-theme-text lbx-theme-${itemName.toLowerCase()} ${fontSize(
                        item.name
                      )} ${moving ? 'transitioning' : ''}`}
                      aria-selected={isItemSelected}
                      aria-label={itemName}
                    >
                      {itemName}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </CssAnimation>
        {!cards[activePosition].crafted && (
          <div className={`${block}__recommended`}>
            {cards[activePosition].recommended && (
              <CssAnimation
                enter={{
                  name: 'short-slide-up-fade-in',
                  duration: 0.5
                }}
                triggerEnter={'in-view-init'}
                inViewProps={{ threshold: 0.2 }}
              >
                <div className={`${block}__recommended-text`}>
                  {cards[activePosition].recommended}
                </div>
              </CssAnimation>
            )}
          </div>
        )}
        {cards[activePosition].crafted && (
          <div className={`${block}__crafted`}>
            <CssAnimation
              enter={{
                name: 'short-slide-up-fade-in',
                duration: 0.5
              }}
              triggerEnter={'in-view-init'}
              inViewProps={{ threshold: 0.2 }}
            >
              <div className={`${block}__crafted-text`}>
                {cards[activePosition].crafted}
              </div>
            </CssAnimation>
          </div>
        )}
        {isMoreThanOneItem && (
          <div className={`${block}__controls`}>
            <CssAnimation
              enter={{
                name: 'long-slide-left-fade-in',
                delay: `${multiplier(0, 0.25)}, hidden`
              }}
              triggerEnter={'in-view-init'}
            >
              <button
                tabIndex={0}
                aria-label='previous atmosphere'
                className={
                  classNames({
                    [`${block}__control`]: true,
                    'control-disabled': leftScrollDisabled
                  })
                }
                onClick={moveLeft}
              >
                <svg
                  className={`${block}__control-circle`}
                  viewBox='0 0 48 48'
                >
                  <circle
                    cx='24'
                    cy='24'
                    r='22'
                    strokeWidth='2px'
                  />
                </svg>
                <svg
                  className={`${block}__control-icon`}
                  xmlns='http://www.w3.org/2000/svg'
                  width='48'
                  height='18'
                  viewBox='0 0 20 18'
                  fill='none'
                >
                  <path
                    d='M12.75 1.5L5.25 9L12.75 16.5'
                    strokeWidth='2'
                    strokeMiterlimit='10'
                    strokeLinecap='round'
                  />
                </svg>
              </button>
            </CssAnimation>

            <CssAnimation
              enter={{
                name: 'slide-up-fade-in',
                duration: 0.5,
                delay: '0.75, hidden',
                easing: 'ease-out'
              }}
              triggerEnter={'in-view-init'}
            >
              <div className={`c-carousel__pagination`}>
                <div className='list-item'>
                  {formatDoubleDigit(activePosition + 1)}
                </div>
                <div className='list-item'>
                  {formatDoubleDigit(items.length)}
                </div>
              </div>
            </CssAnimation>
            <CssAnimation
              enter={{
                name: 'long-slide-left-fade-in',
                delay: `${multiplier(1, 0.25)}, hidden`
              }}
              triggerEnter={'in-view-init'}
            >
              <button
                tabIndex={0}
                aria-label='next atmosphere'
                className={classNames({
                  [`${block}__control`]: true,
                  'control-disabled': rightScrollDisabled
                })}
                onClick={moveRight}
              >
                <svg
                  className={`${block}__control-circle`}
                  viewBox='0 0 48 48'
                >
                  <circle
                    cx='24'
                    cy='24'
                    r='22'
                    strokeWidth='2px'
                  />
                </svg>
                <svg
                  className={`${block}__control-icon`}
                  xmlns='http://www.w3.org/2000/svg'
                  width='48'
                  height='18'
                  viewBox='0 0 16 18'
                  fill='none'
                >
                  <path
                    d='M5.25 1.5L12.75 9L5.25 16.5'
                    strokeWidth='2'
                    strokeMiterlimit='10'
                    strokeLinecap='round'
                  />
                </svg>
              </button>
            </CssAnimation>
          </div>
        )}
      </nav>

      <section className={`${block}__content-container`}>
        {cards.map((item, index) => {
          const personaliseCta = show360ExperienceLinks && _get(item, 'personaliseCta');
          const reserveCta = showReservePrompts && _get(item, 'reserveCta');

          const finalItem = (index + 1 == cards.length);

          return (
            <div
              key={index}
              role='tabpanel'
              aria-hidden={index !== activePosition}
              className={`${block}__item`}
            >
              <div className={`${block}__item-buttons`}>
                {personaliseCta && (
                  <Button
                    href={_get(personaliseCta, 'href', '#')}
                    type='primary'
                    label={_get(personaliseCta, 'label', '')}
                    cssAnimation={true}
                    gtmComponentName={finalItem ? LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceCollection : LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceMatchStyle}
                    gtmComponentGroup={CORE_GTM_TRACKING_VALUES.componentGroup.newCars}
                    gtmLabel={finalItem ? LBX_GTM_TRACKING_VALUES.label.createNewLbx : `style-lbx-${item.name.toLowerCase()}`}
                  />
                )}
                {reserveCta && (
                  <Button
                    href={_get(reserveCta, 'href', '#')}
                    type='tertiary'
                    label={_get(reserveCta, 'label', '')}
                    cssAnimation={true}
                    gtmComponentName={LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceCollection}
                    gtmComponentGroup={CORE_GTM_TRACKING_VALUES.componentGroup.newCars}
                    gtmLabel={`online-car-reservation-${item.name.toLowerCase()}`}
                  />
                )}
              </div>
              <div className={`${block}__images-container`}>
                <AtmosphereHeroImages
                  images={item.images}
                />
              </div>
              {item.isCustom && (
                <div>
                  <DeleteModal
                    copy={deleteModal?.copy}
                    cancelText={deleteModal?.cancelText}
                    confirmText={deleteModal?.confirmText}
                    errorText={deleteModal?.errorText}
                    configId={item.id} 
                    globalTheme={LBX_THEME_MAP[selectedAtmosphere.toLowerCase()]}
                    atmosphere={item.name}
                    user={user}
                    onDeletion={ async () => {
                      setActiveItem(activePosition-1);
                      setActivePosition(activePosition-1);
                    }} />
                </div>
              )}
            </div>
          );
        })}
      </section>
    </LbxTheme>
    
  );
};

Showroom.defaultProps = {
  showReservePrompts:true
}

Showroom.propTypes = {
  startPosition: number,
  title: string,
  subtitle: string,
  items: array,
  onCardChange: func,
  isInfiniteScroll: bool,
  isInfiniteScroll: bool,
  inheritParentTheme: bool,
  selectedAtmosphere:string,
  user: object,
  showReservePrompts:bool
};
